import {
  Badge,
  Box,
  chakra,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Link as ChakraLink,
  Tooltip,
  useDisclosure,
  useMediaQuery,
  useNumberInput,
} from '@chakra-ui/react';
import { magnifyValue } from '@diamond/shared/decimal-util-web';
import { DetailProduct, OrderTemplateItemProduct } from '@diamond/shared/types';
import {
  Button,
  LabelPromoList,
  PromoPriceIntegration,
  Text,
} from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  convertToFloat,
  getItemCardTrackingParams,
  isEven,
  numberWithSeparator,
  trackEvent,
} from '@diamond/shared/utils';
import {
  DEFAULT_OUTLET_NAME_FALLBACK,
  generateFnBEquipmentWhatsAppUrl,
  isFnBEquipmentCategory,
  TRACKING_EVENTS,
} from '@diamond/sol-buyer/constant';
import {
  processPromos,
  useVoucherDisplayEnabled,
} from '@diamond/sol-buyer/utils';
import { useAuthStore, useShipToStore } from '@diamond/sol-buyer-context';
import {
  AddOutlined,
  DeleteOutlined,
  RemoveOutlined,
  WhatsApp,
} from '@mui/icons-material';
import Link, { LinkProps } from 'next/link';
import { useEffect, useState } from 'react';
import shallow from 'zustand/shallow';

import { VoucherAvailableBadge } from '../../voucher/VoucherAvailableBadge';
import { AddToCartPopup } from '../add-to-cart-popup/add-to-cart-popup';
import { ContactSalesModal } from '../contact-sales-modal';
import { Image } from '../image';

type Product<IsOrderTemplateDetail extends boolean> =
  IsOrderTemplateDetail extends true ? OrderTemplateItemProduct : DetailProduct;

export interface CardProductProps<
  IsOrderTemplateDetail extends boolean = false
> {
  productId: string;
  imageUrl: string | undefined;
  productName: string;
  itemCode: number;
  price: number;
  unit: string;
  description: string;
  measurement_unit: string;
  navigateTo: string | LinkProps['href'];
  onClickDelete?: () => void;
  onClickLink?: () => void;
  onClickTambahKeranjang?: () => void;
  onClickAddToOrderTemplate?: () => void;
  handleUpdateItem?: (qty: number, product_id: string) => void;
  withDelete?: boolean;
  name: string;
  isOrderTemplate?: boolean;
  isCounterValue?: boolean;
  stockStatus?: string;
  stock?: number;
  quantity?: number;
  minOrderQty?: number;
  orderQtyMultiple?: number;
  width?: string;
  discountAmount?: number;
  cashbackAmount?: number;
  product?: Product<IsOrderTemplateDetail>;
  isOrderTemplateDetail?: IsOrderTemplateDetail;
  section?: string;
  page?: string;
  sectionId?: string;
  promoSection?: {
    promo_name?: string;
    promo_code?: string;
    mechanism?: string;
  };
  itemIndex?: number;
  sectionIndex?: string;
}

export function CardProduct<IsOrderTemplateDetail extends boolean>({
  productId,
  imageUrl,
  productName,
  itemCode,
  price,
  unit,
  measurement_unit,
  description,
  onClickAddToOrderTemplate,
  onClickDelete,
  onClickLink,
  handleUpdateItem,
  withDelete,
  name,
  isOrderTemplate,
  isCounterValue,
  stockStatus,
  stock,
  quantity,
  minOrderQty,
  orderQtyMultiple,
  width,
  navigateTo,
  product,
  section = '-',
  page = '-',
  sectionId = '-',
  promoSection,
  itemIndex,
  sectionIndex,
}: CardProductProps<IsOrderTemplateDetail>) {
  const [selectedShipToId, selectedShipTo] = useShipToStore(
    (s) => [s.selectedShipToId, s.selectedShipTo],
    shallow
  );
  const token = useAuthStore((s) => s.access_token);
  const isVoucherDisplayEnabled = useVoucherDisplayEnabled();

  const [countDecimal, setCountDecimal] = useState<string>('');
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const processedPromo = processPromos(product);
  const { hasDiscount, hasBundledDiscount, priceAfterDiscount } =
    processedPromo;

  // Check if product is F&B Equipment (case-insensitive)
  const isFnBEquipment = isFnBEquipmentCategory(product?.category_name);

  const productNameTruncate =
    productName.length > 15 && isMobile
      ? `${productName.slice(0, 15)}...`
      : productName;
  const unitPak = unit.length > 9 && isMobile ? `${unit.slice(0, 9)}...` : unit;

  const unitPrice = Number(price);

  const isDisabledCounter =
    typeof minOrderQty !== 'undefined' && typeof stock !== 'undefined'
      ? minOrderQty > stock
      : false;

  const buildItemTrackingParams = (quantity: number) =>
    getItemCardTrackingParams(
      product,
      quantity,
      section,
      page,
      sectionId,
      promoSection,
      {
        ...(itemIndex !== undefined ? { index: itemIndex } : {}),
        ...(sectionIndex !== undefined ? { section_index: sectionIndex } : {}),
      }
    );

  const maxStock = (stock: number, orderQtyMultiple: number) => {
    if (isEven(orderQtyMultiple)) {
      const maxStockGanjil = (
        Math.floor(stock / orderQtyMultiple) * orderQtyMultiple
      ).toFixed(2);
      return parseFloat(maxStockGanjil);
    }
    const maxStockGenap = (
      Math.round(stock / orderQtyMultiple) * orderQtyMultiple
    ).toFixed(2);
    return parseFloat(maxStockGenap);
  };

  const handleTrackSelectPromotion = async (
    prod?: DetailProduct | OrderTemplateItemProduct
  ) => {
    const discountPromo = prod?.discount_promos;
    const bundledDiscountPromo = prod?.bundled_discount_promos;
    const freeGoods = prod?.free_good_promos;
    const bundledFreeGoods = prod?.bundled_free_good_promos;

    const hasAnyPromo =
      (discountPromo?.length || 0) > 0 ||
      (bundledDiscountPromo?.length || 0) > 0 ||
      (freeGoods?.length || 0) > 0 ||
      (bundledFreeGoods?.length || 0) > 0;

    if (hasAnyPromo) {
      const promoParams = buildItemTrackingParams(1);
      trackEvent(TRACKING_EVENTS.SELECT_PROMOTION, {
        promotion_id: promoParams.promotion_id,
        promotion_name: promoParams.promotion_name,
        creative_name: promoParams.promotion_name,
        items: [promoParams],
        section_name: promoParams.section_name,
        page_name: promoParams.page_name,
      });
    }
  };

  const {
    getInputProps,
    getIncrementButtonProps,
    getDecrementButtonProps,
    value: count,
  } = useNumberInput({
    step: orderQtyMultiple,
    defaultValue: quantity,
    min: minOrderQty,
    max: stock && orderQtyMultiple && maxStock(stock, orderQtyMultiple),
    precision: orderQtyMultiple !== 1 ? 2 : 0,
    name: `input-${name}`,
    isDisabled: isDisabledCounter,
    parse: (val) => val.split('.').join('').split(',').join(''),
    format: (val) => numberWithSeparator(val),
    onChange: (val) => {
      const countMagnified = magnifyValue(Number(val), 'stock');
      const stockMagnified = magnifyValue(Number(stock), 'stock');
      if (stock) {
        if (Number(val) > stock) {
          handleUpdateItem?.(stockMagnified.magnifiedValue, productId);
        } else {
          handleUpdateItem?.(countMagnified.magnifiedValue, productId);
        }
      }
    },
  });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  useEffect(() => {
    const numeric = convertToFloat(count);
    const formatted = Number.isInteger(numeric)
      ? numberWithSeparator(String(numeric))
      : numberWithSeparator(numeric.toFixed(2));
    setCountDecimal(formatted);
  }, [count]);

  const handleClickLink = () => {
    onClickLink?.();

    const itemParams = buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000);

    trackEvent(TRACKING_EVENTS.SELECT_ITEM, {
      items: [itemParams],
      item_list_id: itemParams['item_list_id'],
      item_list_name: itemParams['item_list_name'],
      section_name: itemParams['section_name'],
      page_name: itemParams['page_name'],
    });

    handleTrackSelectPromotion(product);
  };

  // Add to Cart Modal logic
  const {
    isOpen: isOpenAddToCart,
    onOpen: onOpenAddToCart,
    onClose: onCloseAddToCart,
  } = useDisclosure();

  const isOutofStock = stockStatus === 'OUT_OF_STOCK' || stock === 0;
  const isPriceZero = price === 0;
  // Only out of stock is truly "unavailable" (disabled). Price zero shows contact sales flow.
  const isUnavailable = isOutofStock;
  const showContactSalesLabel = isPriceZero && !isOutofStock;
  const voucherCount =
    product &&
    'count_voucher' in product &&
    typeof product.count_voucher === 'number'
      ? product.count_voucher
      : 0;
  const showVoucherBadge = isVoucherDisplayEnabled && voucherCount > 0;

  const unavailableLabel = isOutofStock ? 'Stok Habis' : 'Hubungi Sales';

  const isBundled = hasBundledDiscount && !hasDiscount;
  const isQualifiedByQuantity =
    !isBundled &&
    product &&
    product.minimum_order_quantity >=
      product.discount_promos[0]?.minimum_order_quantity;

  // Contact Sales Modal logic (for price zero products)
  const {
    isOpen: isContactSalesOpen,
    onOpen: onOpenContactSales,
    onClose: onCloseContactSales,
  } = useDisclosure();

  const handleContactSalesClick = () => {
    trackEvent(TRACKING_EVENTS.CONTACT_SALES_CTA_CLICK, {
      ...buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000),
      source: 'card',
    });
    trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
      ...buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000),
      source: 'card',
    });
    onOpenContactSales();
  };

  /**
   * Renders the appropriate action button based on product state
   * Refactored from nested ternary for better readability
   */
  const renderActionButton = () => {
    // Case 0: Price is zero (but in stock) - show "Tambah Keranjang" with ContactSalesModal
    if (isPriceZero && !isOutofStock) {
      return (
        <Box
          w={withDelete ? { base: 'full', md: '80%' } : 'full'}
          pt={{ base: '2', md: '0' }}
        >
          <Button
            w="full"
            bg="blue"
            color="white"
            variant="solid"
            _hover={{
              bg: 'navy',
            }}
            size="sm"
            fontSize={{ base: '12px', md: '14px' }}
            onClick={handleContactSalesClick}
            data-item-code={itemCode}
          >
            + Keranjang
          </Button>
          <ContactSalesModal
            isOpen={isContactSalesOpen}
            onClose={onCloseContactSales}
            productId={productId}
            productName={productName}
            source="card"
          />
        </Box>
      );
    }

    // Case 1: Product is out of stock - show "Cari Produk Serupa"
    if (isOutofStock) {
      return (
        <Box
          w={withDelete ? { base: 'full', md: '80%' } : 'full'}
          pt={{ base: '2', md: '0' }}
        >
          <Link
            href={`/product/${productId}/suggestions?cat1=&page=1`}
            passHref
          >
            <Button
              w="full"
              variant="outline"
              size="sm"
              fontSize={{ base: '12px', md: '14px' }}
              onClick={() =>
                trackEvent(
                  TRACKING_EVENTS.CLICK_PRODUCT_RELATED,
                  buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000)
                )
              }
            >
              Cari Produk Serupa
            </Button>
          </Link>
        </Box>
      );
    }

    // Case 2: Order template mode - show "+ Daftar Belanja"
    if (isOrderTemplate) {
      return (
        <Box width={withDelete ? { base: 'full', md: '80%' } : 'full'}>
          <Button
            w="full"
            bg="blue"
            color="white"
            variant="solid"
            size="sm"
            fontSize={{ base: '12px', md: '14px' }}
            _hover={{
              bg: 'navy',
            }}
            onClick={() => {
              if (onClickAddToOrderTemplate) onClickAddToOrderTemplate();
              trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
                ...buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000),
              });
              handleTrackSelectPromotion(product);
            }}
            isDisabled={isDisabledCounter}
          >
            + Daftar Belanja
          </Button>
        </Box>
      );
    }

    // Case 3: F&B Equipment - show "Tanya produk" WhatsApp button
    if (isFnBEquipment) {
      return (
        <Box
          flex={{ base: 'none', md: '1' }}
          w={withDelete ? { base: 'full', md: '80%' } : 'full'}
          pt={{ base: '2', md: '0' }}
        >
          <ChakraLink
            href={generateFnBEquipmentWhatsAppUrl(
              itemCode,
              product?.brand_desc || '',
              description,
              selectedShipTo?.name || DEFAULT_OUTLET_NAME_FALLBACK
            )}
            isExternal
            _hover={{ textDecoration: 'none' }}
            onClick={() => {
              // GA4 e-commerce compliant tracking with items[] array
              const itemParams = buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000);
              trackEvent(TRACKING_EVENTS.GENERATE_LEAD, {
                currency: 'IDR',
                value: itemParams.price || 0,
                lead_source: 'whatsapp',
                items: [itemParams],
              });
            }}
          >
            <Button
              w="full"
              variant="outline"
              borderColor="#0000A3"
              color="#0000A3"
              leftIcon={<Icon as={WhatsApp} />}
              size="sm"
              fontSize={{ base: '12px', md: '14px' }}
              _hover={{
                bg: '#E8E8FF',
                borderColor: '#000080',
                color: '#000080',
              }}
              data-item-code={itemCode}
            >
              Tanya produk
            </Button>
          </ChakraLink>
        </Box>
      );
    }

    // Default: Show "+ Keranjang" button with AddToCartPopup
    return (
      <Box
        flex={{ base: 'none', md: '1' }}
        w={withDelete ? { base: 'full', md: '80%' } : 'full'}
        pt={{ base: '2', md: '0' }}
      >
        <Button
          w="full"
          bg="blue"
          color="white"
          variant="solid"
          _hover={{
            bg: 'navy',
          }}
          size="sm"
          fontSize={{ base: '12px', md: '14px' }}
          onClick={() => {
            onOpenAddToCart();
            trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
              ...buildItemTrackingParams((product?.minimum_order_quantity ?? 1000) / 1000),
            });
            handleTrackSelectPromotion(product);
          }}
          isDisabled={isDisabledCounter}
          data-item-code={itemCode}
        >
          + Keranjang
        </Button>
        <AddToCartPopup
          isOpen={isOpenAddToCart}
          onClose={onCloseAddToCart}
          product={product as CardProductProps['product']}
          productId={productId}
          token={token}
          selectedShipToId={selectedShipToId}
          section={section}
          page={page}
          sectionId={sectionId}
          itemIndex={itemIndex}
          sectionIndex={sectionIndex}
        />
      </Box>
    );
  };

  return (
    <Box
      w="full"
      minWidth={{ base: width ? width : '150px', lg: '100%' }}
      h="full"
      border="1px"
      borderColor="gray.33"
      rounded="lg"
      overflow="hidden"
      position="relative"
      bg="white"
      display="flex"
      flexDir="column"
      justifyContent="space-between"
      data-testid="card-product"
    >
      <Link href={navigateTo} passHref onClick={handleClickLink}>
        <Box display="block">
          <Box position="relative" isolation="isolate">
            {isUnavailable || showContactSalesLabel ? (
              <Badge
                position="absolute"
                bottom="2"
                left="4"
                zIndex="2"
                fontSize="xs"
                fontWeight="semibold"
                px="2"
                py="1"
                borderRadius="md"
              >
                {unavailableLabel}
              </Badge>
            ) : null}

            <Box
              opacity={isOutofStock ? '.3' : '1'}
              my="1"
              w="full"
              h={{ base: 40, md: '200px' }}
              data-testid="product-image-box"
            >
              <Image
                src={imageUrl}
                style={{
                  objectPosition: 'center',
                  objectFit: 'scale-down',
                }}
                alt={productName}
                fill
              />
            </Box>
          </Box>
          {product ? (
            <Box
              display="flex"
              px={{ base: 2, md: 4 }}
              pt={{ base: '8px' }}
              gap="2"
              overflowX="auto"
              __css={{
                scrollbarWidth: 'none',
                '::-webkit-scrollbar': { display: 'none' },
                border: '0 none',
              }}
            >
              {showVoucherBadge ? <VoucherAvailableBadge /> : null}
              <LabelPromoList processedPromo={processedPromo} />
            </Box>
          ) : null}
          <Box
            py={{ base: 2, md: 4 }}
            px={{ base: 2, md: '4' }}
            pt={{ base: 2, md: 2 }}
          >
            <Tooltip label={productName}>
              <chakra.h1 mb="1" fontSize="sm" fontWeight="bold">
                {productNameTruncate}
              </chakra.h1>
            </Tooltip>

            <Text
              h={{ base: 'calc(2 * 21px)', md: 'calc(2 * 22px)' }}
              whiteSpace="initial"
              overflow="hidden"
              textOverflow="ellipsis"
              fontSize={{ base: 'xs', md: 'sm' }}
              wordBreak="break-all"
              mb="1"
              noOfLines={2}
            >
              {description}
            </Text>
            {(hasDiscount || hasBundledDiscount) && product ? (
              <PromoPriceIntegration
                data={product}
                isBundled={hasBundledDiscount && !hasDiscount}
              />
            ) : null}
            <Box>
              <Text
                as={'span'}
                fontWeight="bold"
                fontSize={{ base: 'lg', md: 'xl' }}
              >
                {isPriceZero
                  ? '-'
                  : buyerCurrencyFormatter(
                      hasDiscount && isQualifiedByQuantity
                        ? priceAfterDiscount
                        : unitPrice
                    )}
              </Text>
              {!isPriceZero ? (
                <Text
                  as={'span'}
                  color="gray.75"
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  {' '}
                  / {measurement_unit}
                </Text>
              ) : null}
              <Tooltip label={unit}>
                <chakra.p
                  mt="1"
                  color="gray.75"
                  fontSize={{ base: 'xs', md: 'sm' }}
                >
                  Pak: {unitPak}
                </chakra.p>
              </Tooltip>
              <chakra.p
                mt={1}
                fontSize={{ base: 'xs', md: 'xs' }}
                color="gray.75"
              >
                {itemCode}
              </chakra.p>
            </Box>
          </Box>
        </Box>
      </Link>

      {/* BAGIAN 2: AREA AKSI (TOMBOL-TOMBOL) */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        alignItems="center"
        px={{ base: 2, md: 4 }}
        pb={{ base: 2, md: 4 }}
        gap={2}
        w="full"
      >
        {withDelete && !isMobile ? (
          <Box
            as={Button}
            id="delete-product-desktop"
            size={isCounterValue ? { base: 'sm', md: 'md' } : 'sm'}
            fontSize={isCounterValue ? undefined : { base: '12px', md: '14px' }}
            w={{ base: '5%', md: '15%' }}
            color="red"
            borderColor="red"
            _hover={{ bg: 'red', color: 'white', borderColor: 'red' }}
            onClick={() => onClickDelete && onClickDelete()}
          >
            <Icon
              as={DeleteOutlined}
              w={{ base: '16px', md: '20px' }}
              h={{ base: '16px', md: '20px' }}
            />
          </Box>
        ) : null}
        {isCounterValue && !isUnavailable ? (
          <HStack
            flex={{ base: 'none', md: '1' }}
            borderWidth="1px"
            borderColor="black"
            bottom={{ base: 3, md: 2 }}
            width="full"
            rounded={{ base: 'md', md: 'lg' }}
            height={{ base: '32px', md: '40px' }}
            px="2"
          >
            <Flex justify="flex-start" align="center" flex={1}>
              <IconButton
                aria-label="min"
                name="min"
                icon={<RemoveOutlined fontSize="medium" />}
                size="xs"
                border="1px"
                color="white"
                _hover={{ bgColor: 'zure' }}
                onBlur={(e) => e.preventDefault()}
                {...dec}
              />
            </Flex>
            <Box>
              {orderQtyMultiple && orderQtyMultiple !== 1 ? (
                <Text width="auto" textAlign="center" fontWeight="semibold">
                  {countDecimal}
                </Text>
              ) : (
                <Input
                  fontWeight="semibold"
                  textAlign="center"
                  variant="unstyled"
                  isDisabled={stock === 0}
                  fontSize={{ base: '14px', md: '16px' }}
                  autoComplete="off"
                  {...input}
                />
              )}
            </Box>
            <Flex justify="flex-end" align="center" flex={1}>
              <IconButton
                aria-label="plus"
                name="plus"
                icon={<AddOutlined />}
                size="xs"
                border="1px"
                bgColor="blue"
                color="white"
                _hover={{ bgColor: 'zure' }}
                onBlur={(e) => e.preventDefault()}
                {...inc}
              />
            </Flex>
          </HStack>
        ) : (
          renderActionButton()
        )}
        {withDelete && isMobile ? (
          <Button
            id="delete-product-mobile"
            color="red"
            borderColor="red"
            variant="outline"
            width="full"
            size="sm"
            leftIcon={
              <Icon
                as={DeleteOutlined}
                w={{ base: '16px' }}
                h={{ base: '16px' }}
              />
            }
            onClick={() => onClickDelete && onClickDelete()}
          >
            Hapus
          </Button>
        ) : null}
      </Flex>
    </Box>
  );
}
